import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldAlert, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MIN_BASIS = 20;

function RemoveForm({ api, resolution, onRemoved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [basis, setBasis] = useState('');
  const [effDate, setEffDate] = useState(today);
  const [saving, setSaving] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="bind-block-remove-toggle"
        className="text-[10px] underline mt-1" style={{ color: '#5C6B7A' }}>
        Remove resolution (different underwriter, prospective only)
      </button>
    );
  }
  const short = basis.trim().length < MIN_BASIS;
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/bind-blocks/resolutions/${resolution.resolution_id}/remove`, { basis, effective_date: effDate });
      toast.success(`Resolution removed — effective ${r.data.effective_date}, prospective only`);
      onRemoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Removal refused');
    }
    setSaving(false);
  };
  return (
    <div className="mt-2 p-3 rounded-sm space-y-2" style={{ background: 'rgba(226,75,74,0.04)', border: '1px solid rgba(226,75,74,0.2)' }} data-testid="bind-block-remove-form">
      <p className="text-[10px]" style={{ color: '#B03230' }}>
        Removal widens cover and reverses a deliberate decision: it needs a different person at or above the author's authority, its own basis, and applies from the effective date forward — it never revives cover for a loss already occurred or a claim already notified.
      </p>
      <div className="flex items-center gap-2">
        <span className="text-[10px]" style={{ color: '#5C6B7A' }}>Effective date</span>
        <input type="date" value={effDate} min={today} onChange={e => setEffDate(e.target.value)}
          data-testid="bind-block-remove-date" className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
      </div>
      <textarea value={basis} onChange={e => setBasis(e.target.value)} rows={2} data-testid="bind-block-remove-basis"
        placeholder={`Why is removing this right? (minimum ${MIN_BASIS} characters)`}
        className="w-full px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
      <div className="flex items-center justify-between">
        <span className="text-[10px]" style={{ color: short ? '#E24B4A' : '#1D9E75' }}>{basis.trim().length}/{MIN_BASIS} characters minimum</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="rounded-sm text-xs h-7">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={short || saving} data-testid="bind-block-remove-btn"
            className="rounded-sm text-xs h-7" style={{ background: '#E24B4A' }}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Remove — prospective'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResolveForm({ api, submissionId, trigger, onResolved }) {
  const [outcome, setOutcome] = useState('');
  const [basis, setBasis] = useState('');
  const [conditions, setConditions] = useState('');
  const [saving, setSaving] = useState(false);
  const outcomes = Object.keys(trigger.artefacts || {});
  const short = basis.trim().length < MIN_BASIS;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/submissions/${submissionId}/bind-blocks/resolve`, {
        field_id: trigger.field_id, scope_key: trigger.scope_key || '',
        outcome, basis, conditions,
      });
      toast.success(`Resolved as ${outcome} — recorded against the circumstance, not the policy`);
      onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resolution failed');
    }
    setSaving(false);
  };

  return (
    <div className="mt-2 p-3 rounded-sm space-y-2" style={{ background: 'rgba(26,76,124,0.04)' }} data-testid={`bind-block-resolve-${trigger.field_id}${trigger.scope_key ? '-' + trigger.scope_key : ''}`}>
      <select value={outcome} onChange={e => setOutcome(e.target.value)} data-testid="bind-block-outcome-select"
        className="w-full px-2 py-1.5 border border-input rounded-sm text-xs bg-white">
        <option value="">Select outcome…</option>
        {outcomes.map(o => <option key={o} value={o}>{`${o.replace(/_/g, ' ')} — ${(trigger.artefacts || {})[o] || ''}`}</option>)}
      </select>
      {outcome === 'ACCEPTED_WITH_CONDITIONS' && (
        <textarea value={conditions} onChange={e => setConditions(e.target.value)} rows={2} data-testid="bind-block-conditions-input"
          placeholder="Conditions precedent for this item — these print on GEN-E-003"
          className="w-full px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
      )}
      <textarea value={basis} onChange={e => setBasis(e.target.value)} rows={2} data-testid="bind-block-basis-input"
        placeholder={`Basis for the decision (minimum ${MIN_BASIS} characters) — "OK" is not a reason`}
        className="w-full px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
      <div className="flex items-center justify-between">
        <span className="text-[10px]" style={{ color: short ? '#E24B4A' : '#1D9E75' }} data-testid="bind-block-basis-counter">
          {basis.trim().length}/{MIN_BASIS} characters minimum
        </span>
        <Button size="sm" onClick={submit} disabled={!outcome || short || saving} data-testid="bind-block-resolve-btn"
          className="rounded-sm text-xs h-7" style={{ background: '#1A4C7C' }}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Record resolution'}
        </Button>
      </div>
    </div>
  );
}

export default function BindBlockPanel({ api, submissionId, isStaff, onResolved }) {
  const [gate, setGate] = useState(null);
  const load = useCallback(() => {
    api.get(`/submissions/${submissionId}/bind-blocks`).then(r => setGate(r.data)).catch(() => {});
  }, [api, submissionId]);
  useEffect(() => { load(); }, [load]);

  if (!gate || !(gate.triggers || []).length) return null;
  const open = gate.triggers.filter(t => t.status === 'blocking' || t.status === 'held');

  return (
    <Card className="p-4 rounded-sm bg-white space-y-2" data-testid="bind-block-panel"
      style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: `3px solid ${gate.blocked || open.length ? '#E24B4A' : '#1D9E75'}` }}>
      <div className="flex items-center gap-2">
        {gate.blocked || open.length
          ? <ShieldAlert className="w-4 h-4" style={{ color: '#E24B4A' }} />
          : <CheckCircle2 className="w-4 h-4" style={{ color: '#1D9E75' }} />}
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>
          Bind blocks {gate.blocked ? '— bind is blocked' : open.length ? '— items held' : '— all resolved'}
        </p>
      </div>
      <p className="text-[11px]" style={{ color: '#5C6B7A' }}>
        The quote stands. Binding is gated until each disclosed circumstance carries a named underwriter's outcome and basis. Vehicle-scoped holds block that item only — the rest of the fleet binds. Resolutions attach to the circumstance and re-attach automatically at renewal.
      </p>
      {gate.triggers.map((t, i) => (
        <div key={i} className="pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`bind-block-trigger-${i}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium" style={{ color: '#0D1B2A' }}>
                {t.label} <span className="font-mono text-[10px]" style={{ color: '#5C6B7A' }}>({t.field_id}{t.scope_key ? ` · ${t.scope_key}` : ''})</span>
              </p>
              {t.detail && <p className="text-[11px] mt-0.5 italic" style={{ color: '#5C6B7A' }}>"{t.detail}"</p>}
              {t.resolution && (
                <>
                  <p className="text-[11px] mt-0.5" style={{ color: '#1D9E75' }} data-testid={`bind-block-resolution-${i}`}>
                    {t.resolution.outcome} by {t.resolution.underwriter_name} · {new Date(t.resolution.created_at).toLocaleString('en-AU')}
                    {t.resolution.endorsement_code ? ` · ${t.resolution.endorsement_code}` : ''} — {t.resolution.basis}
                  </p>
                  {isStaff && <RemoveForm api={api} resolution={t.resolution} onRemoved={() => { load(); onResolved && onResolved(); }} />}
                </>
              )}
            </div>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold shrink-0" data-testid={`bind-block-status-${i}`}
              style={{
                background: t.status === 'blocking' ? 'rgba(226,75,74,0.12)' : t.status === 'held' ? 'rgba(232,160,32,0.12)' : 'rgba(29,158,117,0.12)',
                color: t.status === 'blocking' ? '#E24B4A' : t.status === 'held' ? '#B27516' : '#1D9E75',
              }}>
              {t.status.toUpperCase()}
            </span>
          </div>
          {isStaff && (t.status === 'blocking' || t.status === 'held') && (
            <ResolveForm api={api} submissionId={submissionId} trigger={t}
              onResolved={() => { load(); onResolved && onResolved(); }} />
          )}
        </div>
      ))}
    </Card>
  );
}
